// AuthContext — global auth state, single source of truth.
//
// CRITICAL: if the URL hash contains `session_id=`, SKIP the /me check.
// AuthCallback will exchange the session_id and establish the cookie first.
// (See auth playbook · "Global AuthProvider Race Condition".)
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Auth } from "../lib/auth";

const AuthContext = createContext({
  user: null,
  loading: true,
  refresh: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  login: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await Auth.me();
      setUser(me);
      return me;
    } catch (e) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we're arriving back from the OAuth callback, skip /me until the
    // AuthCallback route has exchanged the session_id and set the cookie.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await Auth.logout();
    } catch {
      // swallow — cookie is cleared server-side; UI still transitions
    }
    setUser(null);
  }, []);

  const login = useCallback((next) => {
    Auth.beginLogin(typeof next === "string" ? next : undefined);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout, login }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
